import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCity } from "@/context/CityContext";
import { VendorCard } from "@/components/VendorCard";
import { Search, X } from "lucide-react";

export default function Vendors() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const serviceType = params.get("service_type") || "";
  const search = params.get("search") || "";
  const [q, setQ] = useState(search);
  const { city } = useCity();

  useEffect(() => setQ(search), [search]);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: serviceTypes = [] } = useQuery({
    queryKey: ["service-types"],
    queryFn: async () => (await api.get("/service-types")).data,
  });

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ["vendors", category, serviceType, search, city],
    queryFn: async () =>
      (await api.get("/vendors", {
        params: {
          ...(category ? { category } : {}),
          ...(serviceType ? { service_type: serviceType } : {}),
          ...(search ? { search } : {}),
          ...(city ? { city } : {}),
        },
      })).data,
  });

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setParam("search", q);
  };

  return (
    <div className="px-4 md:px-8 py-6">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">
        Explore vendors
      </h1>
      <p className="text-muted-foreground text-sm mb-5">
        {vendors.length} vendors {city ? `in ${city}` : "nearby"} ready for your celebration
      </p>

      <form onSubmit={submitSearch} className="flex items-center gap-2 mb-4">
        <div className="flex-1 flex items-center gap-2 bg-card border border-stone-200 rounded-full px-4 h-12">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            data-testid="vendors-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vendors or services..."
            className="flex-1 outline-none text-sm bg-transparent"
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); setParam("search", ""); }}>
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>
        <button
          data-testid="vendors-search-btn"
          type="submit"
          className="h-12 px-5 bg-primary text-white rounded-full text-sm font-semibold"
        >
          Search
        </button>
      </form>

      {/* Occasion pills */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-2">
        <button
          data-testid="filter-cat-all"
          onClick={() => setParam("category", "")}
          className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${
            !category ? "bg-primary text-white border-primary" : "border-stone-300 text-muted-foreground hover:bg-muted"
          }`}
        >
          All occasions
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`filter-cat-${c.slug}`}
            onClick={() => setParam("category", c.slug)}
            className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${
              category === c.slug ? "bg-primary text-white border-primary" : "border-stone-300 text-muted-foreground hover:bg-muted"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Service type pills */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
        <button
          onClick={() => setParam("service_type", "")}
          className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
            !serviceType ? "bg-secondary/20 text-secondary-foreground border-secondary" : "border-stone-200 text-muted-foreground hover:bg-muted"
          }`}
        >
          All services
        </button>
        {serviceTypes.map((s) => (
          <button
            key={s}
            data-testid={`filter-svc-${s}`}
            onClick={() => setParam("service_type", s)}
            className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
              serviceType === s ? "bg-secondary/20 text-secondary-foreground border-secondary" : "border-stone-200 text-muted-foreground hover:bg-muted"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : vendors.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="no-vendors">
          No vendors found. Try a different filter.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((v, i) => (
            <VendorCard key={v.id} vendor={v} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
